import { Briefcase, CreditCard, Gift, Heart, Plus, User } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DefinitionBox } from '../../components/onboarding/DefinitionBox';
import { SelectionCountBadge } from '../../components/onboarding/SelectionCountBadge';
import { SelectionGroup } from '../../components/onboarding/SelectionGroup';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { WizardErrorCallout, WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI } from '../../data/auth';
import { sourcesAPI } from '../../data/income';
import {
  groupIncomeSuggestions,
  INCOME_GROUPS,
  ONBOARDING_DEFINITIONS,
  ONBOARDING_INCOME_PRESELECT,
} from '../../lib/onboardingCatalogs';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';
import {
  defaultIconNameForCategory,
  resolveIncomeSourceIcon,
} from '../../utils/incomeSourceIcons';

const EXTRA_INCOME_ICONS = {
  meal_card: CreditCard,
  pension: User,
  subsidies: Gift,
  family_help: Heart,
  other_income: Plus,
};

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

function normalizeName(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

/** Keep API `icon` as string; expose Lucide component as `Icon` for chips. */
export function withIncomeDisplayIcon(item) {
  const iconName =
    typeof item.icon === 'string' && item.icon.trim()
      ? item.icon.trim()
      : defaultIconNameForCategory(item.category);
  return {
    ...item,
    icon: iconName,
    Icon: EXTRA_INCOME_ICONS[item.key] || resolveIncomeSourceIcon({ ...item, icon: iconName }),
  };
}

export function buildIncomeBulkPayload(picked) {
  return picked.map((s) => ({
    name: s.label,
    category: s.category,
    recurrence: s.recurrence,
    icon: typeof s.icon === 'string' ? s.icon : '',
  }));
}

/** Match catalog suggestion keys to already-created sources by name (trim+lower). */
export function matchIncomeKeysFromExisting(allItems, existingSources) {
  const existingNames = new Set(
    (existingSources || []).map((s) => normalizeName(s.name)).filter(Boolean),
  );
  return (allItems || [])
    .filter((item) => existingNames.has(normalizeName(item.label)))
    .map((item) => item.key);
}

/** Drop catalog items whose labels already exist as sources. */
export function filterNewIncomeItems(picked, existingSources) {
  const existingNames = new Set(
    (existingSources || []).map((s) => normalizeName(s.name)).filter(Boolean),
  );
  return (picked || []).filter((item) => !existingNames.has(normalizeName(item.label)));
}

export function OnboardingIncomeSourcesSelect() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const [suggestions, setSuggestions] = useState([]);
  const [existingSources, setExistingSources] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const didHydrate = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [suggestionsRes, listRes] = await Promise.all([
          sourcesAPI.suggestions(),
          sourcesAPI.list(),
        ]);
        if (cancelled) return;
        if (suggestionsRes?.success) {
          setSuggestions(suggestionsRes.data || []);
        }
        if (listRes?.success) {
          setExistingSources(normalizeList(listRes.data));
        }
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { fixed, variable, occasional } = useMemo(() => {
    const grouped = groupIncomeSuggestions(suggestions);
    return {
      fixed: grouped.fixed.map(withIncomeDisplayIcon),
      variable: grouped.variable.map(withIncomeDisplayIcon),
      occasional: grouped.occasional.map(withIncomeDisplayIcon),
    };
  }, [suggestions]);

  const allItems = useMemo(
    () => [...fixed, ...variable, ...occasional],
    [fixed, variable, occasional],
  );
  const itemsByGroup = useMemo(
    () => ({ fixed, variable, occasional }),
    [fixed, variable, occasional],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    if (loadingSuggestions || didHydrate.current || allItems.length === 0) return;
    const matched = matchIncomeKeysFromExisting(allItems, existingSources);
    if (matched.length > 0) {
      setSelected(matched);
    } else {
      const available = new Set(allItems.map((i) => i.key));
      const defaults = ONBOARDING_INCOME_PRESELECT.filter((k) => available.has(k));
      if (defaults.length) {
        setSelected(defaults);
      }
    }
    didHydrate.current = true;
  }, [allItems, existingSources, loadingSuggestions]);

  const toggle = (key) => {
    setSelected((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  };

  const advance = useCallback(
    async (skipCreate = false) => {
      setSaving(true);
      setServerError(null);

      if (!skipCreate && selected.length > 0) {
        const picked = allItems.filter((s) => selected.includes(s.key));
        const toCreate = filterNewIncomeItems(picked, existingSources);
        if (toCreate.length > 0) {
          const payload = buildIncomeBulkPayload(toCreate);
          const response = await sourcesAPI.bulkCreate(payload);
          if (!response.success) {
            setServerError(
              typeof response.error === 'string'
                ? response.error
                : response.error?.detail || pt.auth.errors.generic,
            );
            setSaving(false);
            return;
          }
        }
      }

      await authAPI.saveOnboardingStep('categories');
      await refreshMe();
      setSaving(false);
      navigate('/onboarding/categories');
    },
    [allItems, existingSources, navigate, refreshMe, selected],
  );

  const onContinue = useCallback(() => advance(false), [advance]);
  const onSkip = useCallback(() => advance(true), [advance]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    onSkip,
    skipLabel: pt.onboarding.common.skipLater,
    loading: saving,
  });

  if (loadingSuggestions) {
    return <SectionSkeleton variant="list" />;
  }

  return (
    <div className="flex flex-col gap-space-6">
      <div className="flex items-start justify-between gap-3">
        <WizardStepHeader
          eyebrow={pt.onboarding.incomeSources.eyebrow}
          title={pt.onboarding.incomeSources.title}
          subtitle={pt.onboarding.incomeSources.subtitle}
          className="flex-1"
        />
        <SelectionCountBadge count={selected.length} className="mt-7 shrink-0" />
      </div>

      <DefinitionBox icon={Briefcase}>{ONBOARDING_DEFINITIONS.income}</DefinitionBox>

      {INCOME_GROUPS.map((group) => {
        const items = itemsByGroup[group.id] || [];
        return (
          <SelectionGroup
            key={group.id}
            name={group.name}
            pctLabel={group.pctLabel}
            description={group.description}
            dotClass={group.dotClass}
            tint={group.tint}
            items={items}
            selectedKeys={selectedSet}
            getKey={(item) => item.key}
            onToggle={(key) => toggle(key)}
          />
        );
      })}

      {serverError ? <WizardErrorCallout>{serverError}</WizardErrorCallout> : null}
    </div>
  );
}
